import React, { useEffect } from "react";
import PropTypes from "prop-types";
import Box from "../wrappers/Box";
import FormHelperText from "../wrappers/FormHelperText";
import FormLabel from "../wrappers/FormLabel";
import InputCurrency from "../InputCurrency";
import InputAdornment from "../wrappers/InputAdornment";
import Slider from "../wrappers/Slider";
import TextField from "../wrappers/TextField";
import Typography from "../wrappers/Typography";

/** Converte texto de entrada para número finito ou nulo. */
const pvNormalizeNumber = (pValue) => {
  if (pValue === "") {
    return null;
  }

  const xValue = Number(pValue);
  return Number.isFinite(xValue) ? xValue : null;
};

/** Resolve a mensagem de erro da faixa controlada. */
const pvGetErrorMessage = (pValue, pMin, pMax) => {
  if (pValue.min !== null && pValue.max !== null && pValue.min > pValue.max) {
    return "O mínimo não pode ser maior que o máximo.";
  }

  if (
    [pValue.min, pValue.max].some((pItem) => pItem !== null && pMin !== undefined && pItem < pMin)
  ) {
    return `Os valores devem ser maiores ou iguais a ${pMin}.`;
  }

  if (
    [pValue.min, pValue.max].some((pItem) => pItem !== null && pMax !== undefined && pItem > pMax)
  ) {
    return `Os valores devem ser menores ou iguais a ${pMax}.`;
  }

  return undefined;
};

/** Limita uma posição visual aos bounds do Slider. */
const pvClamp = (pValue, pMin, pMax) => Math.min(Math.max(pValue, pMin), pMax);

const wBRLFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Formata um valor numérico em real brasileiro com espaço estável. */
const pvFormatBRL = (pValue) => wBRLFormatter.format(pValue).replace(/\u00a0/g, " ");

/**
 * Renderiza uma faixa numérica controlada, sem publicar texto formatado.
 *
 * @param {object} pProps Propriedades do componente.
 * @param {string} pProps.id Identificador estável do filtro.
 * @param {string} pProps.label Rótulo-base da faixa.
 * @param {"range"|"min-only"|"max-only"} pProps.mode Modo de limites visíveis.
 * @param {{min: number|null, max: number|null}} pProps.value Valor controlado.
 * @param {Function} pProps.onChange Recebe somente números ou nulos.
 * @param {number} [pProps.min] Limite absoluto inferior.
 * @param {number} [pProps.max] Limite absoluto superior.
 * @param {number} [pProps.step=1] Incremento dos campos.
 * @param {string} [pProps.prefix] Prefixo apenas visual.
 * @param {string} [pProps.suffix] Sufixo apenas visual.
 * @param {"BRL"} [pProps.currency] Ativa apresentação monetária em real brasileiro; o default efetivo é undefined.
 * @param {boolean} [pProps.disabled=false] Desabilita a edição.
 * @param {boolean} [pProps.showSlider=true] Exibe o Slider quando os bounds são válidos.
 * @param {boolean} [pProps.showTextField=true] Exibe os campos numéricos.
 * @param {"normal"|"inverted"|false} [pProps.track="normal"] Define a apresentação do track.
 * @param {{toValue: Function, fromValue: Function, step: number}} [pProps.sliderScale] Conversão bidirecional da posição visual.
 * @param {Function} [pProps.onValidityChange] Recebe a validade atual.
 * @returns {React.ReactElement}
 */
const SearchFilterRange = (pProps) => {
  const {
    id,
    label,
    mode,
    value,
    onChange,
    min,
    max,
    step = 1,
    prefix,
    suffix,
    currency,
    disabled = false,
    showSlider = true,
    showTextField = true,
    track = "normal",
    sliderScale,
    onValidityChange,
  } = pProps;
  const xErrorMessage = pvGetErrorMessage(value, min, max);
  const xIsInvalid = Boolean(xErrorMessage);
  const xErrorId = `${id}-error`;
  const xLabelId = `${id}-label`;
  const xShowMin = mode !== "max-only";
  const xShowMax = mode !== "min-only";
  const xIsBRL = currency === "BRL";
  const xHasSliderBounds = Number.isFinite(min) && Number.isFinite(max) && min <= max;
  const xFromValue = sliderScale ? sliderScale.fromValue : (pValue) => pValue;
  const xToValue = sliderScale ? sliderScale.toValue : (pValue) => pValue;
  const xSliderMin = xHasSliderBounds ? xFromValue(min) : undefined;
  const xSliderMax = xHasSliderBounds ? xFromValue(max) : undefined;
  const xHasVisualBounds =
    xHasSliderBounds &&
    Number.isFinite(xSliderMin) &&
    Number.isFinite(xSliderMax) &&
    xSliderMin <= xSliderMax;

  useEffect(() => {
    if (onValidityChange) {
      onValidityChange(!xIsInvalid);
    }
  }, [onValidityChange, xIsInvalid]);

  /** Atualiza somente o limite editado no contrato numérico público. */
  const pvHandleChange = (pKey, pEvent) => {
    onChange({ ...value, [pKey]: pvNormalizeNumber(pEvent.target.value) });
  };

  /** Adapta a máscara monetária para o contrato público numérico e anulável. */
  const pvHandleCurrencyChange = (pKey, pEvent, pNumericValue) => {
    const xNumericValue = Number(pNumericValue);
    const xIsNegative = pEvent.target.value.trim().startsWith("-");
    const xValue =
      pEvent.target.value === "" || !Number.isFinite(xNumericValue)
        ? null
        : Math.abs(xNumericValue) * (xIsNegative ? -1 : 1);

    onChange({ ...value, [pKey]: xValue });
  };

  /** Resolve a posição visual sem corrigir o valor público controlado. */
  const pvGetSliderPosition = (pKey) => {
    const xFallback = pKey === "min" ? xSliderMin : xSliderMax;
    const xDomainValue = value[pKey];

    if (!Number.isFinite(xDomainValue)) {
      return xFallback;
    }

    const xPosition = xFromValue(xDomainValue);
    return Number.isFinite(xPosition) ? pvClamp(xPosition, xSliderMin, xSliderMax) : xFallback;
  };

  // Mantém a chave semântica junto da posição que o MUI ordena visualmente.
  const xSliderRangeEntries =
    mode === "range"
      ? [
          { key: "min", position: pvGetSliderPosition("min") },
          { key: "max", position: pvGetSliderPosition("max") },
        ].sort((pFirst, pSecond) => pFirst.position - pSecond.position)
      : [];

  /** Identifica a chave pública correspondente ao thumb ativo. */
  const pvGetSliderKey = (pThumbIndex) => {
    if (mode === "range") {
      return xSliderRangeEntries[pThumbIndex].key;
    }

    return mode === "max-only" ? "max" : "min";
  };

  /** Publica no domínio real somente o limite movimentado. */
  const pvHandleSliderChange = (pEvent, pSliderValue, pActiveThumb) => {
    const xKey = pvGetSliderKey(pActiveThumb);
    const xPosition = Array.isArray(pSliderValue) ? pSliderValue[pActiveThumb] : pSliderValue;

    onChange({ ...value, [xKey]: xToValue(xPosition) });
  };

  /** Formata a posição visual como valor de domínio acessível. */
  const pvFormatSliderValue = (pPosition) => {
    const xValue = xToValue(pPosition);
    const xFormattedValue = xIsBRL ? pvFormatBRL(xValue) : `${prefix || ""}${xValue}`;

    return `${xFormattedValue}${suffix || ""}`;
  };

  /** Monta o campo numérico de cada limite visível. */
  const pvRenderInput = (pKey, pInputLabel) => (
    <TextField
      disabled={disabled}
      error={xIsInvalid}
      fullWidth
      variant="outlined"
      inputProps={{
        min,
        max,
        step,
        currency: xIsBRL ? currency : undefined,
        decimal: xIsBRL ? 2 : undefined,
        "aria-describedby": xIsInvalid ? xErrorId : undefined,
      }}
      InputProps={{
        inputComponent: xIsBRL ? InputCurrency : undefined,
        startAdornment: xIsBRL ? (
          <InputAdornment position="start">R$</InputAdornment>
        ) : prefix ? (
          <InputAdornment position="start">{prefix}</InputAdornment>
        ) : undefined,
        endAdornment: suffix ? <InputAdornment position="end">{suffix}</InputAdornment> : undefined,
      }}
      label={pInputLabel}
      onChange={
        xIsBRL
          ? (pEvent, pNumericValue) => pvHandleCurrencyChange(pKey, pEvent, pNumericValue)
          : (pEvent) => pvHandleChange(pKey, pEvent)
      }
      type={xIsBRL ? undefined : "number"}
      value={value[pKey] ?? ""}
      size="small"
      margin="dense"
      sx={{
        "& input[type='number']": {
          colorScheme: "dark",
        },
      }}
    />
  );

  return (
    <Box>
      <FormLabel disabled={disabled} id={xLabelId} sx={{ mb: 0.5 }}>
        <Typography variant="caption" fontWeight={500}>
          {label}
        </Typography>
      </FormLabel>
      <Box
        aria-label={label}
        aria-labelledby={xLabelId}
        role="group"
        sx={{ display: "flex", flexDirection: "column", gap: 1 }}
      >
        {showTextField ? (
          <Box sx={{ display: "flex", gap: 1 }}>
            {xShowMin ? pvRenderInput("min", "Mínimo") : null}
            {xShowMax ? pvRenderInput("max", "Máximo") : null}
          </Box>
        ) : null}
        {showSlider && xHasVisualBounds ? (
          <Box sx={{ px: 1 }}>
            <Slider
              disabled={disabled}
              disableSwap
              getAriaLabel={(pIndex) => (pvGetSliderKey(pIndex) === "min" ? "Mínimo" : "Máximo")}
              getAriaValueText={pvFormatSliderValue}
              max={xSliderMax}
              min={xSliderMin}
              onChange={pvHandleSliderChange}
              step={sliderScale ? sliderScale.step : step}
              track={track}
              value={
                mode === "range"
                  ? xSliderRangeEntries.map((pEntry) => pEntry.position)
                  : pvGetSliderPosition(mode === "max-only" ? "max" : "min")
              }
              valueLabelDisplay="auto"
              valueLabelFormat={pvFormatSliderValue}
              size="small"
            />
          </Box>
        ) : null}
      </Box>
      {xIsInvalid ? (
        <FormHelperText id={xErrorId} role="alert" error>
          {xErrorMessage}
        </FormHelperText>
      ) : null}
    </Box>
  );
};

SearchFilterRange.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(["range", "min-only", "max-only"]).isRequired,
  value: PropTypes.shape({ min: PropTypes.number, max: PropTypes.number }).isRequired,
  onChange: PropTypes.func.isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  currency: PropTypes.oneOf(["BRL"]),
  disabled: PropTypes.bool,
  showSlider: PropTypes.bool,
  showTextField: PropTypes.bool,
  track: PropTypes.oneOf(["normal", "inverted", false]),
  sliderScale: PropTypes.shape({
    toValue: PropTypes.func.isRequired,
    fromValue: PropTypes.func.isRequired,
    step: PropTypes.number.isRequired,
  }),
  onValidityChange: PropTypes.func,
};

export default SearchFilterRange;
